import { Title, Text, Stack, SimpleGrid, Card, Group, Badge, Box, Loader, Center, useMantineTheme } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiFetch } from "../api/client.js";
import type { Ereignis } from "@iso27001/shared";
import { useAuth } from "../auth/AuthContext.js";
import { useEnumLabel } from "../i18n/enum-label.js";

interface DashboardData {
    ereignisse: { total: number; open: number; last30d: number };
    risiken: { total: number; byClass: { mäßig: number; hoch: number; kritisch: number }; inTreatment: number; criticalCount: number };
    massnahmen: { total: number; overdue: number; open: number; completed: number };
}

interface ListResp<T> { items: T[]; total: number; page: number; pageSize: number; }

/** A single KPI card with a big number, caption, and optional click navigation. */
function KpiCard({ title, value, caption, color, onClick }: {
    title: string;
    value: number | undefined;
    caption?: string;
    color: string;
    onClick?: () => void;
}) {
    return (
        <Card
            withBorder
            padding="lg"
            radius="md"
            style={{ cursor: onClick ? "pointer" : "default", borderLeft: `4px solid var(--mantine-color-${color}-6)` }}
            onClick={onClick}
        >
            <Text size="xs" tt="uppercase" fw={700} c="dimmed" mb={4}>{title}</Text>
            {value == null
                ? <Loader size="sm" color={color} />
                : <Text size="2rem" fw={900} c={color}>{value}</Text>
            }
            {caption && <Text size="xs" c="dimmed" mt={4}>{caption}</Text>}
        </Card>
    );
}

/** Three-segment proportional bar for risk classes. */
function RiskClassBar({ byClass, total }: { byClass: { mäßig: number; hoch: number; kritisch: number }; total: number }) {
    const { label } = useEnumLabel();
    const safeTotal = total > 0 ? total : 1;
    const segments = [
        { key: "mäßig", count: byClass.mäßig, color: "green" },
        { key: "hoch", count: byClass.hoch, color: "yellow" },
        { key: "kritisch", count: byClass.kritisch, color: "red" },
    ] as const;

    return (
        <Stack gap="xs">
            <Group gap={0} style={{ borderRadius: 4, overflow: "hidden", height: 20 }}>
                {segments.map(({ key, count, color }) => {
                    const pct = Math.round((count / safeTotal) * 100);
                    return pct > 0 ? (
                        <Box
                            key={key}
                            style={{ width: `${pct}%`, backgroundColor: `var(--mantine-color-${color}-6)`, height: "100%" }}
                            title={`${label("riskClass", key)}: ${count}`}
                        />
                    ) : null;
                })}
            </Group>
            <Group gap="sm">
                {segments.map(({ key, count, color }) => (
                    <Group key={key} gap={4}>
                        <Badge color={color} size="sm" variant="filled">{count}</Badge>
                        <Text size="xs" c="dimmed">{label("riskClass", key)}</Text>
                    </Group>
                ))}
            </Group>
        </Stack>
    );
}

export function DashboardPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const theme = useMantineTheme();
    void theme; // used implicitly via CSS vars
    const { t } = useTranslation();
    const { label } = useEnumLabel();

    const { data } = useQuery({
        queryKey: ["dashboard"],
        queryFn: () => apiFetch<DashboardData>("/api/dashboard"),
    });

    // Fetch last 4 open Ereignisse for the "recent" list
    const { data: recentEreignisse } = useQuery({
        queryKey: ["ereignisse", { status: "Offen", pageSize: 4 }],
        queryFn: () => apiFetch<ListResp<Ereignis>>("/api/ereignisse?status=Offen&pageSize=4"),
    });

    // Fetch massnahmen — overdue ones are identified client-side
    const { data: openMassnahmen } = useQuery({
        queryKey: ["massnahmen", { status: "Offen", pageSize: 20 }],
        queryFn: () => apiFetch<ListResp<{ id: number; code: string; description: string; dueDate: number | null; status: string }>>("/api/massnahmen?status=Offen&pageSize=20"),
    });

    const now = Math.floor(Date.now() / 1000);
    const overdueMassnahmen = (openMassnahmen?.items ?? [])
        .filter((m) => m.dueDate != null && m.dueDate < now)
        .slice(0, 4);

    return (
        <Stack>
            <Group>
                <Title order={2}>{t("dashboard.title")}</Title>
                <Text c="dimmed" size="sm">{t("dashboard.welcome", { name: user?.name ?? "" })}</Text>
            </Group>

            {/* KPI cards — 4 columns desktop, 2 tablet, 1 mobile */}
            <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
                {/*
                  Captions render only once `data` has arrived, so `count` is
                  always a number. i18next skips plural resolution entirely
                  when count is a string — the old `?? "…"` placeholder would
                  make a pluralised caption fall through to a key with no
                  singular/plural form and render the raw key on screen. The
                  card already shows a Loader in place of the value while
                  loading, so nothing is left blank without an indicator.
                */}
                <KpiCard
                    title={t("dashboard.kpi.openEvents.title")}
                    value={data?.ereignisse.open}
                    caption={data ? t("dashboard.kpi.openEvents.caption", { count: data.ereignisse.last30d }) : undefined}
                    color="blue"
                    onClick={() => navigate("/ereignisse?status=Offen")}
                />
                <KpiCard
                    title={t("dashboard.kpi.criticalRisks.title")}
                    value={data?.risiken.criticalCount}
                    caption={data ? t("dashboard.kpi.criticalRisks.caption", { count: data.risiken.total }) : undefined}
                    color="red"
                    onClick={() => navigate("/risiken?riskClass=kritisch")}
                />
                <KpiCard
                    title={t("dashboard.kpi.overdueMassnahmen.title")}
                    value={data?.massnahmen.overdue}
                    caption={data ? t("dashboard.kpi.overdueMassnahmen.caption", { count: data.massnahmen.open }) : undefined}
                    color="yellow"
                    onClick={() => navigate("/massnahmen")}
                />
                <KpiCard
                    title={t("dashboard.kpi.risksInTreatment.title")}
                    value={data?.risiken.inTreatment}
                    caption={data ? t("dashboard.kpi.risksInTreatment.caption", { count: data.risiken.byClass.kritisch }) : undefined}
                    color="teal"
                    onClick={() => navigate("/risiken?status=InBehandlung")}
                />
            </SimpleGrid>

            {/* Second row: risk class bar + recent lists */}
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Card withBorder padding="lg" radius="md">
                    <Text size="xs" tt="uppercase" fw={700} c="dimmed" mb="sm">{t("dashboard.card.risksByClass")}</Text>
                    {data ? (
                        <RiskClassBar byClass={data.risiken.byClass} total={data.risiken.total} />
                    ) : (
                        <Center><Loader size="sm" /></Center>
                    )}
                </Card>

                <Card withBorder padding="lg" radius="md">
                    <Text size="xs" tt="uppercase" fw={700} c="dimmed" mb="sm">{t("dashboard.card.recentEvents")}</Text>
                    {recentEreignisse == null ? (
                        <Center><Loader size="sm" /></Center>
                    ) : recentEreignisse.items.length === 0 ? (
                        <Text size="sm" c="dimmed">{t("dashboard.card.noOpenEvents")}</Text>
                    ) : (
                        <Stack gap={6}>
                            {recentEreignisse.items.map((e) => (
                                <Group key={e.id} justify="space-between" wrap="nowrap">
                                    <Text
                                        size="sm"
                                        style={{ cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                        onClick={() => navigate("/ereignisse")}
                                    >
                                        <Text component="span" fw={600} mr={6}>{e.code}</Text>
                                        {e.description.length > 50 ? e.description.slice(0, 50) + "…" : e.description}
                                    </Text>
                                    <Badge size="xs" color="blue" variant="light" style={{ flexShrink: 0 }}>{label("ereignisStatus", e.status)}</Badge>
                                </Group>
                            ))}
                        </Stack>
                    )}
                </Card>
            </SimpleGrid>

            {/* Overdue Maßnahmen row */}
            {overdueMassnahmen.length > 0 && (
                <Card withBorder padding="lg" radius="md">
                    <Text size="xs" tt="uppercase" fw={700} c="dimmed" mb="sm">{t("dashboard.card.overdueMassnahmen")}</Text>
                    <Stack gap={6}>
                        {overdueMassnahmen.map((m) => (
                            <Group key={m.id} justify="space-between" wrap="nowrap">
                                <Text
                                    size="sm"
                                    style={{ cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                    onClick={() => navigate("/massnahmen")}
                                >
                                    <Text component="span" fw={600} mr={6}>{m.code}</Text>
                                    {m.description.length > 80 ? m.description.slice(0, 80) + "…" : m.description}
                                </Text>
                                <Badge size="xs" color="yellow" variant="light" style={{ flexShrink: 0 }}>{t("dashboard.card.overdueBadge")}</Badge>
                            </Group>
                        ))}
                    </Stack>
                </Card>
            )}
        </Stack>
    );
}
